/* eslint-disable static-service/require-stories -- we have stories for the Markdown component */
import {StyleSheet} from "aphrodite";

import SimpleMarkdown from "@khanacademy/simple-markdown";
import {addStyle} from "@khanacademy/wonder-blocks-core";
import {spacing} from "@khanacademy/wonder-blocks-tokens";

import type {MarkdownRule} from "../types.ts";

import {blockStyle} from "./shared-styles.ts";

const StyledTable = addStyle("table");
const StyledTh = addStyle("th");
const StyledTr = addStyle("tr");
const StyledTd = addStyle("td");

// Output rule for handling tables:
// Ex.
// | 00 | 10 | 20 | 30 |
// |----|:---|:--:|---:|
// | 01 | 11 | 21 | 31 |
// | 02 | 12 | 22 | 32 |
export const tableRule: Readonly<MarkdownRule> = {
    ...SimpleMarkdown.defaultRules.table,
    react: (node, output, state) => {
        const headers = node.header.map((content, colIndex) => (
            <StyledTh
                align={node.align[colIndex]}
                key={`col-${colIndex}`}
                scope="col"
                style={[styles.markdownTh, styles.bordered]}
            >
                {output(content, state)}
            </StyledTh>
        ));

        const numCol = headers.length;
        const rows = node.cells.map((row, rowIndex) => (
            <StyledTr
                key={`row-${rowIndex}`}
                style={rowIndex % 2 ? styles.shadedTr : null}
            >
                {row.map((content, colIndex) => {
                    // We ignore extraneous row columns i.e. if the number of
                    // columns in a row exceed the number of columns of the
                    // table, we don't render them.
                    return colIndex < numCol ? (
                        <StyledTd
                            align={node.align[colIndex]}
                            key={`${rowIndex}-${colIndex}`}
                            style={[styles.markdownTd, styles.bordered]}
                        >
                            {output(content, state)}
                        </StyledTd>
                    ) : null;
                })}
            </StyledTr>
        ));

        return (
            <StyledTable key={state.key} style={styles.markdownTable}>
                <thead>
                    <StyledTr>{headers}</StyledTr>
                </thead>
                <tbody>{rows}</tbody>
            </StyledTable>
        );
    },
};

const CELL_PADDING_STYLE = `${spacing.xxSmall_6}px ${spacing.small_12}px`;

const styles = StyleSheet.create({
    markdownTable: {
        display: "block",
        width: "max-content",
        maxWidth: "100%",
        overflow: "auto",
        ...blockStyle,
    },
    markdownTh: {
        fontWeight: 600,
        padding: CELL_PADDING_STYLE,
    },
    markdownTd: {
        padding: CELL_PADDING_STYLE,
    },
    shadedTr: {
        backgroundColor: "rgba(175,184,193,0.2)",
    },
    bordered: {
        border: `1px solid rgba(175,184,193,0.5)`,
    },
});
